import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Course,
  CourseLesson,
  CourseProgressDto,
  LessonResource,
  QuizSubmitResponse,
} from "../../types/course.types";
import {
  courseApi,
  toEmbedUrl,
  isEmbedReliable,
  countPublishedLessons,
  getYoutubeId,
  isYouTube,
} from "../../services/course.service";
import YouTubePlayer from "./YouTubePlayer";
import { useT, useI18n } from "../../i18n";

interface FlatLesson {
  lesson: CourseLesson;
  sectionId: string;
  sectionTitle: string;
  globalIndex: number; // 1-based index among lessons
}

const EMPTY_PROGRESS: CourseProgressDto = {
  completedLessonIds: [],
  quizScores: {},
  enrolledAt: null,
  unlockedLessonIds: [],
  sectionUnlocks: {},
  percentComplete: 0,
};

export default function CoursePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, code } = useI18n();

  const [course, setCourse] = useState<Course | null>(null);
  const [progress, setProgress] = useState<CourseProgressDto>(EMPTY_PROGRESS);
  const [activeLessonId, setActiveLessonId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const [marking, setMarking] = useState(false);

  // Quiz state (per active lesson)
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [quizResult, setQuizResult] = useState<QuizSubmitResponse | null>(null);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/academy");
  };

  useEffect(() => {
    if (!id) return;
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [courseRes, progressRes] = await Promise.allSettled([
          courseApi.getCourseById(id),
          courseApi.getProgress(id),
        ]);
        if (!active) return;
        if (courseRes.status === "fulfilled") {
          setCourse(courseRes.value.data);
        } else {
          throw new Error(t("academy.course.loadFailed"));
        }
        if (progressRes.status === "fulfilled") {
          setProgress({ ...EMPTY_PROGRESS, ...progressRes.value.data });
        }
      } catch (err: any) {
        if (active)
          setError(
            t("academy.course.loadFailed")
          );
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [id]);

  // Flatten lessons in section/lesson order.
  const flat: FlatLesson[] = useMemo(() => {
    const out: FlatLesson[] = [];
    let counter = 0;
    (course?.sections || []).forEach((s) => {
      (s.lessons || []).forEach((lesson) => {
        counter += 1;
        out.push({
          lesson,
          sectionId: s.id,
          sectionTitle: s.title,
          globalIndex: counter,
        });
      });
    });
    return out;
  }, [course]);

  const completed = useMemo(
    () => new Set(progress.completedLessonIds || []),
    [progress.completedLessonIds]
  );

  // Compute the unlocked set on the client (deterministic, doesn't depend on the
  // server returning correct gating): a lesson is unlocked only if the PREVIOUS
  // lesson (in flat order) is completed — the first lesson is always open — AND
  // its section's schedule (if any) has been released. We intersect with the
  // server's unlockedLessonIds when present so the server can only be stricter.
  const sectionUnlocks = progress.sectionUnlocks || {};
  const unlocked = useMemo(() => {
    const serverSet = new Set(progress.unlockedLessonIds || []);
    const hasServer = (progress.unlockedLessonIds || []).length > 0;
    const out = new Set<string>();
    let prevId: string | null = null;
    for (const f of flat) {
      const prevDone = prevId === null || completed.has(prevId);
      const sectionLocked = !!sectionUnlocks[f.sectionId];
      const localUnlocked = prevDone && !sectionLocked;
      // Honor the server too (if it provided data) so it can only tighten.
      if (localUnlocked && (!hasServer || serverSet.has(f.lesson.id))) {
        out.add(f.lesson.id);
      }
      prevId = f.lesson.id;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flat, completed, progress.unlockedLessonIds, progress.sectionUnlocks]);

  // On open, resume where the user left off: the first UNLOCKED lesson that
  // isn't completed yet (the "current" lesson). Falls back to the last unlocked
  // lesson (e.g. course finished), then the very first lesson.
  useEffect(() => {
    if (activeLessonId || flat.length === 0) return;
    const firstIncompleteUnlocked = flat.find(
      (f) => unlocked.has(f.lesson.id) && !completed.has(f.lesson.id)
    );
    const unlockedList = flat.filter((f) => unlocked.has(f.lesson.id));
    const lastUnlocked = unlockedList[unlockedList.length - 1];
    const target = firstIncompleteUnlocked || lastUnlocked || flat[0];
    setActiveLessonId(target.lesson.id);
  }, [flat, unlocked, completed, activeLessonId]);

  // Reset quiz state when switching lessons.
  useEffect(() => {
    setAnswers({});
    setQuizResult(null);
  }, [activeLessonId]);

  const total = countPublishedLessons(course || ({ sections: [] } as any));
  const doneCount = flat.filter((f) => completed.has(f.lesson.id)).length;
  const percent =
    typeof progress.percentComplete === "number"
      ? progress.percentComplete
      : total === 0
      ? 0
      : Math.round((doneCount / total) * 100);

  const activeFlat = flat.find((f) => f.lesson.id === activeLessonId);
  const activeLesson = activeFlat?.lesson;
  const activeIdx = activeFlat ? flat.indexOf(activeFlat) : -1;

  // Is a lesson accessible? Server-provided unlockedLessonIds is authoritative.
  const isUnlocked = (lesson: CourseLesson): boolean => unlocked.has(lesson.id);

  // Build a lock reason for a locked lesson (presentation only).
  const lockReason = (index: number): string => {
    const item = flat[index];
    if (!item) return t("academy.course.locked");
    // Schedule: section locked until a release date ("Coming soon" + date).
    const releaseIso = progress.sectionUnlocks?.[item.sectionId];
    if (releaseIso) {
      const d = new Date(releaseIso);
      if (!isNaN(d.getTime())) {
        return t("academy.course.comingSoonAvailable", {
          date: d.toLocaleDateString(code, {
            weekday: "short",
            month: "short",
            day: "numeric",
          }),
        });
      }
      return t("academy.course.comingSoon");
    }
    // Require-completion: prior lesson must be complete.
    const prev = flat[index - 1]?.lesson;
    if (prev && prev.settings?.requireCompletion && !completed.has(prev.id)) {
      return t("academy.course.lockedTooltip", { title: prev.title });
    }
    return t("academy.course.locked");
  };

  const applyProgress = (p: CourseProgressDto) =>
    setProgress({ ...EMPTY_PROGRESS, ...p });

  // Move to the lesson after the given one (used after auto/marking complete).
  const advanceAfter = (lessonId: string) => {
    const idx = flat.findIndex((f) => f.lesson.id === lessonId);
    const next = idx >= 0 ? flat[idx + 1] : undefined;
    if (next) setActiveLessonId(next.lesson.id);
  };

  const markComplete = async (
    lesson: CourseLesson,
    value: boolean,
    advance = false
  ) => {
    if (!id) return;
    setMarking(true);
    try {
      const res = await courseApi.setLessonCompletion(id, lesson.id, value);
      applyProgress(res.data);
      if (value && advance) advanceAfter(lesson.id);
    } catch (err: any) {
      // Surface gating rejection (400 "Lesson is locked") without crashing.
      setError(t("academy.course.progressError"));
      setTimeout(() => setError(null), 3000);
    } finally {
      setMarking(false);
    }
  };

  // Called when a YouTube video finishes: auto-complete + advance to next.
  const handleVideoEnded = (lesson: CourseLesson) => {
    if (completed.has(lesson.id)) {
      // Already complete — just move on.
      advanceAfter(lesson.id);
    } else {
      markComplete(lesson, true, true);
    }
  };

  const submitQuiz = async (lesson: CourseLesson) => {
    if (!id || !lesson.questions) return;
    const ordered = lesson.questions.map((q) =>
      answers[q.id] != null ? answers[q.id] : -1
    );
    setSubmittingQuiz(true);
    try {
      const res = await courseApi.submitQuiz(id, {
        lessonId: lesson.id,
        answers: ordered,
      });
      setQuizResult(res.data);
      if (res.data.progress) applyProgress(res.data.progress);
    } catch (err: any) {
      setError(t("academy.course.quiz.submitError"));
      setTimeout(() => setError(null), 3000);
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const handleResourceDownload = async (r: LessonResource) => {
    if (!r.fileKey) {
      window.open(r.url, "_blank");
      return;
    }
    try {
      setDownloadingKey(r.fileKey);
      const res = await courseApi.getResourceDownloadUrl(r.fileKey);
      if (res.data?.downloadUrl) window.open(res.data.downloadUrl, "_blank");
    } catch {
      window.open(r.url, "_blank");
    } finally {
      setDownloadingKey(null);
    }
  };

  const goNext = () => {
    if (activeIdx < 0) return;
    const next = flat[activeIdx + 1];
    if (next) setActiveLessonId(next.lesson.id);
  };
  const nextLesson = activeIdx >= 0 ? flat[activeIdx + 1] : undefined;
  const nextUnlocked = nextLesson ? isUnlocked(nextLesson.lesson) : false;

  const lessonTypeIcon = (l: CourseLesson) =>
    l.type === "quiz"
      ? "fa-circle-question"
      : l.type === "text"
      ? "fa-file-lines"
      : "fa-play";

  return (
    <div className="flex flex-col flex-1 h-full bg-gray-50">
      {/* Header */}
      <header className="h-16 bg-white border-b border-gray-200 flex items-center gap-4 px-6 shrink-0 sticky top-0 z-10 shadow-sm">
        <button
          onClick={handleBack}
          className="text-gray-500 hover:text-gray-700"
          title={t("common.back")}
        >
          <i className="fa-solid fa-arrow-left"></i>
        </button>
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-gray-900 truncate">
            {course?.title || t("academy.course.courseFallback")}
          </h1>
          <p className="text-xs text-gray-500">
            {t("academy.course.progressSummary", { done: doneCount, total, percent })}
          </p>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto py-6 px-6">
        {loading && (
          <div className="w-full aspect-video bg-gray-200 rounded-2xl animate-pulse" />
        )}
        {error && (
          <p className="text-sm text-red-600 mb-4 bg-red-50 border border-red-100 rounded-lg px-4 py-2">
            {error}
          </p>
        )}

        {!loading && course && flat.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            {t("academy.course.noLessons")}
          </div>
        )}

        {!loading && course && flat.length > 0 && (
          <div className="w-full flex flex-col lg:flex-row gap-8">
            {/* Left: content area */}
            <div className="flex-1 space-y-6 min-w-0">
              {activeLesson && (
                <LessonContent
                  lesson={activeLesson}
                  reliable={isEmbedReliable(activeLesson.source)}
                  embedUrl={
                    activeLesson.type === "video" && activeLesson.videoUrl
                      ? toEmbedUrl(activeLesson.videoUrl, activeLesson.source)
                      : ""
                  }
                  onVideoEnded={() => handleVideoEnded(activeLesson)}
                  answers={answers}
                  onAnswer={(qid, idx) =>
                    setAnswers((p) => ({ ...p, [qid]: idx }))
                  }
                  quizResult={quizResult}
                  submittingQuiz={submittingQuiz}
                  onSubmitQuiz={() => submitQuiz(activeLesson)}
                  bestQuizScore={progress.quizScores?.[activeLesson.id]}
                />
              )}

              {activeLesson && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">
                        {activeLesson.title}
                      </h2>
                      <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-3">
                        <span className="capitalize">
                          <i
                            className={`fa-solid ${lessonTypeIcon(
                              activeLesson
                            )} mr-1`}
                          ></i>
                          {t(`academy.course.lessonTypes.${activeLesson.type}`)}
                        </span>
                        {activeLesson.duration && (
                          <span>
                            <i className="fa-regular fa-clock mr-1"></i>
                            {activeLesson.duration}
                          </span>
                        )}
                      </p>
                      {activeLesson.description && (
                        <p className="text-sm text-gray-600 mt-2">
                          {activeLesson.description}
                        </p>
                      )}
                    </div>

                    {/* Mark complete only for video/text. Quiz completes via submit. */}
                    {activeLesson.type !== "quiz" && (
                      <button
                        disabled={marking}
                        onClick={() => {
                          const completing = !completed.has(activeLesson.id);
                          // Marking complete auto-advances to the next lesson.
                          markComplete(activeLesson, completing, completing);
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors flex items-center gap-2 disabled:opacity-60 ${
                          completed.has(activeLesson.id)
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : "bg-brand-600 text-white hover:bg-brand-700"
                        }`}
                      >
                        <i
                          className={`fa-solid ${
                            completed.has(activeLesson.id)
                              ? "fa-circle-check"
                              : "fa-check"
                          }`}
                        ></i>
                        {completed.has(activeLesson.id)
                          ? activeLesson.type === "text"
                            ? t("academy.course.read")
                            : t("academy.course.completed")
                          : activeLesson.type === "text"
                          ? t("academy.course.markAsRead")
                          : t("academy.course.markComplete")}
                      </button>
                    )}

                    {activeLesson.type === "quiz" &&
                      completed.has(activeLesson.id) && (
                        <span className="px-4 py-2 rounded-lg text-sm font-semibold bg-green-50 text-green-700 border border-green-200 flex items-center gap-2">
                          <i className="fa-solid fa-circle-check"></i>
                          {t("academy.course.passed")}
                        </span>
                      )}
                  </div>

                  {activeLesson.type === "video" &&
                    !isEmbedReliable(activeLesson.source) &&
                    activeLesson.videoUrl && (
                      <p className="mt-3 text-sm text-gray-500">
                        {t("academy.course.playbackFallback")}{" "}
                        <a
                          href={activeLesson.videoUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-brand-600 font-medium hover:underline"
                        >
                          {t("academy.course.openInNewTab")}
                        </a>
                        .
                      </p>
                    )}

                  {/* Resources */}
                  {activeLesson.resources &&
                    activeLesson.resources.length > 0 && (
                      <div className="mt-6">
                        <h3 className="text-sm font-bold text-gray-900 mb-3">
                          {t("academy.course.resources")}
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {activeLesson.resources.map((r, idx) => {
                            const isFile = r.type === "file";
                            const inner = (
                              <>
                                <div
                                  className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                                    isFile
                                      ? "bg-red-50 text-red-500"
                                      : "bg-blue-50 text-blue-500"
                                  }`}
                                >
                                  <i
                                    className={`fa-solid ${
                                      isFile ? "fa-file-arrow-down" : "fa-link"
                                    }`}
                                  ></i>
                                </div>
                                <div className="ml-3 flex-1 min-w-0 text-left">
                                  <p className="text-sm font-semibold text-gray-900 truncate">
                                    {r.title}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {isFile
                                      ? `${r.fileType || t("academy.course.file")}${
                                          r.fileSize ? ` • ${r.fileSize}` : ""
                                        }`
                                      : t("academy.course.externalLink")}
                                  </p>
                                </div>
                              </>
                            );
                            const cls =
                              "flex items-center p-3 rounded-xl border border-gray-100 hover:border-brand-200 hover:bg-brand-50 transition-all w-full";
                            return isFile ? (
                              <button
                                key={idx}
                                onClick={() => handleResourceDownload(r)}
                                disabled={downloadingKey === r.fileKey}
                                className={cls}
                              >
                                {inner}
                              </button>
                            ) : (
                              <a
                                key={idx}
                                href={r.url}
                                target="_blank"
                                rel="noreferrer"
                                className={cls}
                              >
                                {inner}
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  {/* Next button — only shown once the next lesson is unlocked.
                      While locked it's hidden entirely (no greyed "Locked" button);
                      completing the current lesson reveals it. */}
                  {nextLesson && nextUnlocked && (
                    <div className="mt-6 flex justify-end">
                      <button
                        onClick={goNext}
                        className="px-4 py-2 rounded-lg bg-[#111827] text-white text-sm font-semibold hover:bg-gray-800"
                      >
                        {t("academy.course.nextLesson")}{" "}
                        <i className="fa-solid fa-arrow-right ml-1"></i>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right: section-grouped playlist */}
            <div className="w-full lg:w-80 shrink-0">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                    {t("academy.course.courseContent")}
                  </h3>
                  <span className="text-xs text-gray-500">{percent}%</span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mb-4">
                  <div
                    className="h-full bg-green-500 rounded-full transition-all"
                    style={{ width: `${percent}%` }}
                  />
                </div>

                <div className="space-y-4">
                  {(course.sections || []).map((section) => {
                    const visible = section.lessons || [];
                    if (visible.length === 0) return null;
                    const sectionLockedIso =
                      progress.sectionUnlocks?.[section.id];
                    return (
                      <div key={section.id}>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 px-1 flex items-center gap-1.5">
                          {section.title || t("academy.course.defaultSectionTitle")}
                          {sectionLockedIso && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full normal-case tracking-normal">
                              <i className="fa-solid fa-lock text-[9px]"></i>
                              {t("academy.course.comingSoon")}
                            </span>
                          )}
                        </p>
                        <ol className="space-y-1">
                          {visible.map((l) => {
                            const fl = flat.find((f) => f.lesson.id === l.id);
                            const order = fl?.globalIndex ?? 0;
                            const idxInFlat = fl ? flat.indexOf(fl) : -1;
                            const isDone = completed.has(l.id);
                            const isActive = l.id === activeLessonId;
                            const unlockedL = isUnlocked(l);
                            return (
                              <li key={l.id}>
                                <button
                                  onClick={() =>
                                    unlockedL && setActiveLessonId(l.id)
                                  }
                                  disabled={!unlockedL}
                                  title={
                                    unlockedL
                                      ? undefined
                                      : lockReason(idxInFlat)
                                  }
                                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                                    isActive
                                      ? "bg-brand-50 border border-brand-100"
                                      : unlockedL
                                      ? "hover:bg-gray-50"
                                      : "opacity-60 cursor-not-allowed"
                                  }`}
                                >
                                  <span
                                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs shrink-0 ${
                                      isDone
                                        ? "bg-green-500 text-white"
                                        : !unlockedL
                                        ? "bg-gray-100 text-gray-400"
                                        : isActive
                                        ? "bg-brand-600 text-white"
                                        : "bg-gray-100 text-gray-500"
                                    }`}
                                  >
                                    {isDone ? (
                                      <i className="fa-solid fa-check"></i>
                                    ) : !unlockedL ? (
                                      <i className="fa-solid fa-lock text-[10px]"></i>
                                    ) : (
                                      order
                                    )}
                                  </span>
                                  <span className="flex-1 min-w-0">
                                    <span
                                      className={`block text-sm truncate ${
                                        isActive
                                          ? "font-semibold text-brand-700"
                                          : "text-gray-800"
                                      }`}
                                    >
                                      <i
                                        className={`fa-solid ${lessonTypeIcon(
                                          l
                                        )} text-[10px] text-gray-400 mr-1.5`}
                                      ></i>
                                      {l.title}
                                    </span>
                                    {!unlockedL ? (
                                      <span className="text-xs text-gray-400">
                                        {lockReason(idxInFlat)}
                                      </span>
                                    ) : (
                                      l.duration && (
                                        <span className="text-xs text-gray-400">
                                          {l.duration}
                                        </span>
                                      )
                                    )}
                                  </span>
                                </button>
                              </li>
                            );
                          })}
                        </ol>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Lesson content renderer: video | text | quiz
// -------------------------------------------------------------------
const LessonContent: React.FC<{
  lesson: CourseLesson;
  reliable: boolean;
  embedUrl: string;
  onVideoEnded: () => void;
  answers: Record<string, number>;
  onAnswer: (questionId: string, optionIdx: number) => void;
  quizResult: QuizSubmitResponse | null;
  submittingQuiz: boolean;
  onSubmitQuiz: () => void;
  bestQuizScore?: number;
}> = ({
  lesson,
  embedUrl,
  onVideoEnded,
  answers,
  onAnswer,
  quizResult,
  submittingQuiz,
  onSubmitQuiz,
  bestQuizScore,
}) => {
  const t = useT();
  if (lesson.type === "video") {
    const ytId = isYouTube(lesson.videoUrl, lesson.source)
      ? getYoutubeId(lesson.videoUrl)
      : null;
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="relative aspect-video bg-gray-900">
          {ytId ? (
            // YouTube: use the IFrame API so we can auto-complete on video end.
            <YouTubePlayer
              key={lesson.id}
              videoId={ytId}
              title={lesson.title}
              onEnded={onVideoEnded}
            />
          ) : embedUrl ? (
            <iframe
              key={lesson.id}
              src={embedUrl}
              title={lesson.title}
              className="absolute inset-0 w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white/50">
              {t("academy.course.noVideo")}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (lesson.type === "text") {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
        <div
          className="prose prose-sm max-w-none text-gray-800"
          // textBody is HTML authored in the Admin builder.
          dangerouslySetInnerHTML={{
            __html: lesson.textBody || `<p>${t("academy.course.noContent")}</p>`,
          }}
        />
      </div>
    );
  }

  // quiz
  const questions = lesson.questions || [];
  const passPercent = lesson.passPercent ?? 70;
  const correctByQuestion = quizResult?.correctByQuestion;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-gray-900">
          <i className="fa-solid fa-circle-question text-brand-600 mr-2"></i>
          {t("academy.course.quiz.title")}
        </h2>
        <span className="text-xs text-gray-500">
          {t("academy.course.quiz.passMark", { percent: passPercent })}
          {typeof bestQuizScore === "number" && (
            <span className="ml-2 font-semibold text-gray-700">
              {t("academy.course.quiz.best", { percent: bestQuizScore })}
            </span>
          )}
        </span>
      </div>

      {quizResult && (
        <div
          className={`mb-6 rounded-xl border p-4 ${
            quizResult.passed
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          <p className="font-semibold">
            <i
              className={`fa-solid ${
                quizResult.passed ? "fa-circle-check" : "fa-circle-xmark"
              } mr-2`}
            ></i>
            {quizResult.passed
              ? t("academy.course.quiz.passed")
              : t("academy.course.quiz.notPassedYet")}{" "}
            —{" "}
            {t("academy.course.quiz.scoreSummary", {
              score: quizResult.scorePercent,
              correct: quizResult.correctCount,
              total: quizResult.totalQuestions,
            })}
          </p>
          {!quizResult.passed && (
            <p className="text-sm mt-1">
              {t("academy.course.quiz.needToPass", { percent: quizResult.passPercent })}
            </p>
          )}
        </div>
      )}

      <div className="space-y-6">
        {questions.map((q, qi) => {
          const selected = answers[q.id];
          const qCorrect = correctByQuestion ? correctByQuestion[qi] : undefined;
          return (
            <div key={q.id}>
              <p className="font-semibold text-gray-900 mb-3">
                {qi + 1}. {q.prompt}
                {qCorrect != null && (
                  <i
                    className={`fa-solid ml-2 ${
                      qCorrect
                        ? "fa-circle-check text-green-600"
                        : "fa-circle-xmark text-red-600"
                    }`}
                  ></i>
                )}
              </p>
              <div className="space-y-2">
                {q.options.map((opt, oi) => {
                  const isSel = selected === oi;
                  return (
                    <label
                      key={oi}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                        isSel
                          ? "border-brand-300 bg-brand-50"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name={q.id}
                        checked={isSel}
                        onChange={() => onAnswer(q.id, oi)}
                        className="accent-brand-600"
                      />
                      <span className="text-sm text-gray-800">{opt}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={onSubmitQuiz}
          disabled={submittingQuiz || questions.length === 0}
          className="px-5 py-2.5 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-60"
        >
          {submittingQuiz ? (
            <>
              <i className="fa-solid fa-spinner fa-spin mr-2"></i>{t("academy.course.quiz.submitting")}
            </>
          ) : quizResult ? (
            t("academy.course.quiz.submitAgain")
          ) : (
            t("academy.course.quiz.submitAnswers")
          )}
        </button>
      </div>
    </div>
  );
};
